import React, { useEffect, useState } from "react";
import Threshold24h from "./Threshold24h";
const API = process.env.REACT_APP_API_BASE_URL ?? "https://zoompay.co.ke";

const getToken = () => localStorage.getItem("adminToken");

const customerNumber = (u) => {
  const country = (u?.country || "CA").toUpperCase();
  const prefix = country === "US" ? "ZP-US" : "ZP-CA";
  const compact = (u?.id || "").replace(/-/g, "").toUpperCase();
  return compact ? `${prefix}-${compact.slice(0, 8)}` : `${prefix}-UNKNOWN`;
};

export default function CockpitCompliance() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/v2/cockpit/users`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json())
      .then(d => { if (d.ok) setUsers(d.users || d.data || []); })
      .finally(() => setLoading(false));
  }, []);

  const Check = ({ val }) => val
    ? <span className="text-green-600 font-bold">v</span>
    : <span className="text-red-400">x</span>;

  const riskLevel = (u) => {
    const checks = [u.emailVerified, u.phoneVerified, u.idVerified, u.bankVerified].filter(Boolean).length;
    if (checks === 4) return <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">LOW</span>;
    if (checks >= 2) return <span className="px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-800">MEDIUM</span>;
    return <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-800">HIGH</span>;
  };

  const verified = users.filter(u => u.idVerified).length;
  const unverified = users.filter(u => !u.emailVerified).length;

  return (
    <div className="p-6 space-y-6">

      {selectedUser && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setSelectedUser(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-4 max-h-screen overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Customer Profile</h2>
              <button onClick={() => setSelectedUser(null)} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>

            <div className="space-y-2 text-sm">
              {[
                ["Customer #", customerNumber(selectedUser)],
                ["Name", `${selectedUser.firstName || ""} ${selectedUser.lastName || ""}`.trim() || "—"],
                ["Email", selectedUser.email || "—"],
                ["Phone", selectedUser.phone || selectedUser.phoneNumber || "—"],
                ["Country", selectedUser.country || "—"],
                ["Account Status", selectedUser.status ? selectedUser.status.toUpperCase() : "—"],
                ["Customer Since", selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleDateString("en-CA") : "—"],
                ["Email Verified", selectedUser.emailVerified ? "YES" : "NO"],
                ["Phone Verified", selectedUser.phoneVerified ? "YES" : "NO"],
                ["Identity Verified", selectedUser.idVerified ? "YES" : "NO"],
                ["Bank Verified", selectedUser.bankVerified ? "YES" : "NO"],
                ["SNPL Limit", `$${parseFloat(selectedUser.snplLimit || 0).toFixed(2)}`],
                ["SNPL Available", `$${parseFloat(selectedUser.snplAvailable || 0).toFixed(2)}`],
                ["SNPL Used", `$${(parseFloat(selectedUser.snplLimit || 0) - parseFloat(selectedUser.snplAvailable || 0)).toFixed(2)}`],
                ["Developer ID", selectedUser.id ? selectedUser.id.slice(0, 8) + "..." : "—"],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between border-b pb-1 gap-4">
                  <span className="text-gray-500 w-1/3">{label}</span>
                  <span className="text-right w-2/3 break-all">{value}</span>
                </div>
              ))}
            </div>

            <div className="border-t pt-4 mt-4 space-y-2">
              <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Support Actions</p>
              <button
                disabled
                className="w-full bg-gray-100 text-gray-400 py-2 px-4 rounded text-sm font-semibold cursor-not-allowed"
              >
                Send Secure Update Link — Coming Soon
              </button>
              <p className="text-xs text-gray-400">
                CS should send secure customer update links for address, expired ID, or phone changes. Direct edits require Compliance/Admin approval and audit logging.
              </p>
            </div>
          </div>
        </div>
      )}

      <div>
        <h1 className="text-2xl font-bold">Compliance & KYC</h1>
        <p className="text-gray-500 text-sm">User verification status - FINTRAC AML monitoring</p>
      </div>

      <Threshold24h />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Users", value: users.length, color: "bg-blue-50 border-blue-200" },
          { label: "ID Verified", value: verified, color: "bg-green-50 border-green-200" },
          { label: "Unverified Email", value: unverified, color: "bg-red-50 border-red-200" },
          { label: "High Risk", value: users.filter(u => !u.emailVerified && !u.idVerified).length, color: "bg-orange-50 border-orange-200" },
        ].map(c => (
          <div key={c.label} className={`border rounded-lg p-4 ${c.color}`}>
            <div className="text-2xl font-bold">{c.value}</div>
            <div className="text-xs text-gray-600 mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              {["Customer #","User","Country","Email","Phone","ID","Bank","SNPL Limit","SNPL Used","Risk","Status","Joined","Actions"].map(h => (
                <th key={h} className="px-3 py-3 text-left text-gray-600 font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading && <tr><td colSpan={13} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>}
            {users.map(u => (
              <tr key={u.id} className="hover:bg-gray-50">
                <td className="px-3 py-3 font-mono text-xs">{customerNumber(u)}</td>
                <td className="px-3 py-3">
                  <div className="font-medium">{u.firstName} {u.lastName}</div>
                  <div className="text-xs text-gray-400">{u.email}</div>
                </td>
                <td className="px-3 py-3">{u.country}</td>
                <td className="px-3 py-3 text-center"><Check val={u.emailVerified} /></td>
                <td className="px-3 py-3 text-center"><Check val={u.phoneVerified} /></td>
                <td className="px-3 py-3 text-center"><Check val={u.idVerified} /></td>
                <td className="px-3 py-3 text-center"><Check val={u.bankVerified} /></td>
                <td className="px-3 py-3">${parseFloat(u.snplLimit || 0).toFixed(0)}</td>
                <td className="px-3 py-3">${(parseFloat(u.snplLimit || 0) - parseFloat(u.snplAvailable || 0)).toFixed(0)}</td>
                <td className="px-3 py-3">{riskLevel(u)}</td>
                <td className="px-3 py-3">
                  <span className={`px-2 py-0.5 rounded text-xs ${u.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {u.status?.toUpperCase()}
                  </span>
                </td>
                <td className="px-3 py-3 text-gray-500">{new Date(u.createdAt).toLocaleDateString('en-CA')}</td>
                <td className="px-3 py-3">
                  <button
                    onClick={() => setSelectedUser(u)}
                    className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
